import { toNodeNumberCell } from '../stores/utils/node-number';
import type { GetProStudioNodesQuery } from '@pypestream/api-services';
import type {
  ProStudioNode,
  SolutionViewSettings,
} from '@pypestream/pro-studio-collab-client';

import type { ColumnsWidths, TableNode } from '../stores/table-store';

/**
 * Projects the collab document into the payload the table's own load path
 * already takes (PSTUDIO-4450).
 *
 * The Workflows table is written against the Hasura row: a numeric `order`,
 * numeric node numbers, and grouping and collapse kept in a `settings` bag.
 * Rather than repoint every reader, the collab nodes are reshaped into that
 * row and handed to `processVersionData` / `mergeVersionData`, which keep
 * doing the subRows reconcile, collapse visibility, search index and the
 * set-if-changed merge for both sources.
 *
 * Type-only imports from the collab client: this module is in the main chunk,
 * and the client itself must stay in its lazy one.
 */
export type CollabVersionData = {
  solution_version_nodes_ps: GetProStudioNodesQuery['tenancy_solution_version_nodes_ps'];
  columnWidths: ColumnsWidths | null;
  columnPinningSettings: Record<string, unknown> | null;
  columnWrapSettings: Record<string, unknown> | null;
};

// Wide enough that the table's own integer insert-between math never runs out
// of room between two collab rows.
const ORDER_STEP = 1000;

const toNumber = (value: string): number | null => {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const asObject = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

// The bounds the column resize handle enforces (`header-cell.tsx`).
const MIN_COLUMN_WIDTH = 50;
const MAX_COLUMN_WIDTH = 800;

/** Finite widths only, clamped — anything else in the doc would break layout. */
const toColumnWidths = (value: unknown): ColumnsWidths | null => {
  const widths = asObject(value);
  if (!widths) return null;
  const out: Record<string, number> = {};
  for (const [column, width] of Object.entries(widths)) {
    if (typeof width === 'number' && Number.isFinite(width)) {
      out[column] = Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(MIN_COLUMN_WIDTH, width)
      );
    }
  }
  return out as ColumnsWidths;
};

const toRow = (
  node: ProStudioNode,
  index: number,
  context: { collapsed: ReadonlySet<string>; solutionVersionId: string }
): TableNode => {
  const { collapsed, solutionVersionId } = context;
  const {
    nodeNumber,
    nodeType,
    nextNodes,
    cssClassName,
    isSupervisorConfig,
    groupId,
    groupName,
    groupColor,
    isTask,
    order: _order,
    ...fields
  } = node;
  const type = nodeType.toLowerCase();
  const isGroup = type === 'g';

  const settings: NonNullable<TableNode['settings']> = {};
  if (isSupervisorConfig !== null) {
    settings.shouldSyncWithSupervisorConfig = isSupervisorConfig;
  }
  if (groupId !== null) settings.groupId = groupId;
  // PSTUDIO-4451: under live editing a task IS its node, so the node's id is
  // the task id the table's task lookups (`settings.taskId`) find it by.
  if (isTask) settings.taskId = node.id;
  if (isGroup) {
    if (groupName !== null) settings.groupName = groupName;
    settings.groupColor = groupColor;
    settings.groupExpanded = !collapsed.has(node.id);
  }

  return {
    ...fields,
    nodeType: type,
    order: (index + 1) * ORDER_STEP,
    // PSTUDIO-4453: the table holds the canonical string too — never a
    // `Number()` of it, which read "1e3" as 1000 and rounded long numbers.
    nodeNumber: toNodeNumberCell(nodeNumber),
    // A reference that is not a number is kept as written, for validation to
    // report rather than for this projection to drop.
    nextNodes: nextNodes.map((ref) => toNumber(ref) ?? ref),
    cssClassname: cssClassName,
    solutionVersionId,
    settings,
  } as unknown as TableNode;
};

export const toVersionData = (
  orderedNodes: readonly ProStudioNode[],
  viewSettings: SolutionViewSettings,
  solutionVersionId: string
): CollabVersionData => {
  const collapsed = new Set(viewSettings.collapsedGroupIds);
  return {
    solution_version_nodes_ps: orderedNodes.map((node, index) =>
      toRow(node, index, { collapsed, solutionVersionId })
    ) as unknown as CollabVersionData['solution_version_nodes_ps'],
    columnWidths: toColumnWidths(viewSettings.columnWidths),
    columnPinningSettings: asObject(viewSettings.columnPinningSettings),
    columnWrapSettings: asObject(viewSettings.columnWrapSettings),
  };
};
